import { css } from '@emotion/react';
import { getDisplayName } from '@vmos2/utils/src/getDisplayName';
import React, { forwardRef } from 'react';
import type { ComponentType, ForwardedRef } from 'react';

import type { Margin, Padding } from './types';

type Props = Margin & Padding;
type Ref = ForwardedRef<ComponentType>;

const CSSNames = {
  m: ['margin'],
  mt: ['margin-top'],
  mr: ['margin-right'],
  mb: ['margin-bottom'],
  ml: ['margin-left'],
  mx: ['margin-left', 'margin-right'],
  my: ['margin-top', 'margin-bottom'],
  p: ['padding'],
  pt: ['padding-top'],
  pr: ['padding-right'],
  pb: ['padding-bottom'],
  pl: ['padding-left'],
  px: ['padding-left', 'padding-right'],
  py: ['padding-top', 'padding-bottom'],
};

export const getSpacingStyles = (props: Props) => {
  const getStyle = (key: keyof typeof CSSNames, value: typeof props[keyof typeof CSSNames]) => {
    const cssKeys = CSSNames[key as keyof Props];
    const cssValue = typeof value === 'number' ? `${value * 4}px` : 'auto';

    return cssKeys.map(cssKey => `${cssKey}: ${cssValue}`).join(';\n');
  };

  return (Object.keys(CSSNames) as (keyof typeof CSSNames)[])
    .map((cssName) => typeof props[cssName] !== 'undefined' && getStyle(cssName, props[cssName]))
    .filter(Boolean)
    .join(';\n');
};

export const withSpacingStyles = (EnhancedComponent: ComponentType<Props & { ref: Ref }>) => {
  const WrapperComponent = forwardRef((props: Props, ref: Ref) => {
    const { m, mt, mr, mb, ml, mx, my, ...rest } = props;
    const styles = getSpacingStyles(props);

    return <EnhancedComponent {...rest} ref={ref} {...(styles && { css: css`${styles}` })} />;
  });

  WrapperComponent.displayName = `withSpacingStyles(${getDisplayName(EnhancedComponent)})`;

  return WrapperComponent;
};
